import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSimilarSuppliers, type SimilarSupplier } from '../../../shared/api/suppliers.api'

// Подсказка дублей поставщика (этап 3, кусок C3): под полем имени поставщика
// показывает «похоже на WTR — это он?». Ничего не блокирует: можно одним
// кликом взять существующего или ответить «нет, это другой» — тогда имя
// сохранится как ввели.

/** Короче — не спрашиваем: на одну-две буквы похоже слишком многое. */
const MIN_NAME_LENGTH = 3
const DEBOUNCE_MS = 350

const keyOf = (s: string) => s.trim().toLowerCase()

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

const chipCls =
  'px-1.5 py-0.5 rounded border border-amber-200 bg-white font-medium hover:bg-amber-100 transition-colors'

export function SupplierDuplicateHint({
  name,
  onPick,
  onOpen,
  excludeId,
}: {
  /** Что сейчас введено в поле. */
  name: string
  /** Подставить имя существующего поставщика в поле (квоты, окно запроса). */
  onPick?: (name: string) => void
  /** Открыть карточку существующего вместо создания дубля (справочник). */
  onOpen?: (id: string) => void
  /** Карточка переименовывается — сама себе она не дубль. */
  excludeId?: string
}) {
  const typed = name.trim()
  const debounced = useDebouncedValue(typed, DEBOUNCE_MS)
  // Ответ «нет, это другой» — для конкретного введённого имени; стоит его
  // поменять, подсказка вернётся.
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)

  const { data = [] } = useQuery({
    queryKey: ['suppliers', 'similar', keyOf(debounced), excludeId ?? null],
    queryFn: () => getSimilarSuppliers(debounced, excludeId),
    enabled: debounced.length >= MIN_NAME_LENGTH,
    staleTime: 30_000,
    // 403 у ролей без подсказки или сбой сети — просто молчим, ввод не мешаем.
    retry: false,
  })

  // Пока печатают, старый ответ не показываем: он про другое имя.
  if (typed.length < MIN_NAME_LENGTH || keyOf(typed) !== keyOf(debounced)) return null
  if (dismissedKey === keyOf(typed) || data.length === 0) return null

  const act = (s: SimilarSupplier) => (onOpen ? onOpen(s.id) : onPick?.(s.name))
  const actTitle = (s: SimilarSupplier) =>
    onOpen ? `Открыть карточку «${s.name}»` : `Подставить «${s.name}» — квота уйдёт ему`

  const first = data[0]!
  // Имя уже в справочнике: в квоте всё сойдётся само, спрашивать нечего.
  // В справочнике — предупреждаем заранее, сервер такой дубль не создаст.
  if (first.match === 'exact') {
    if (!onOpen) return null
    return (
      <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 flex flex-wrap items-center gap-1.5">
        <span>Поставщик «{first.name}» уже есть в справочнике.</span>
        <button type="button" onClick={() => act(first)} className={chipCls}>
          Открыть карточку
        </button>
      </div>
    )
  }

  if (first.match === 'alias') {
    return (
      <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 flex flex-wrap items-center gap-1.5">
        <span>
          «{first.viaAlias}» — второе имя поставщика «{first.name}» (дубли объединены).
        </span>
        <button
          type="button"
          onClick={() => act(first)}
          title={actTitle(first)}
          className={chipCls}
        >
          {onOpen ? 'Открыть карточку' : `Взять «${first.name}»`}
        </button>
      </div>
    )
  }

  return (
    <div
      role="status"
      className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 flex flex-wrap items-center gap-1.5"
    >
      <span>Похоже на</span>
      {data.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => act(s)}
          title={actTitle(s)}
          className={chipCls}
        >
          {s.name}
          {s.viaAlias && <span className="font-normal"> (он же {s.viaAlias})</span>}
        </button>
      ))}
      <span>— это он?</span>
      <button
        type="button"
        onClick={() => setDismissedKey(keyOf(typed))}
        className="ml-auto text-amber-700 hover:underline"
      >
        Нет, это другой
      </button>
    </div>
  )
}
